import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Box,
  Button,
  Grid,
  Avatar,
  Stack,
  Pagination,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import instance from "../utils/axiosInstance";
import NFTCard from "../components/NFTCard";
import { getMetaFromTokenURI, getIpfsUrl } from "../utils/meta_in_token";

const API_BASE_URL = process.env.REACT_APP_BACK_URL;



const ProfilePage = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [preview, setPreview] = useState("");
  const [nfts, setNfts] = useState([]);
  const [tab, setTab] = useState("all");
  const [message, setMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [likedNFTs, setLikedNFTs] = useState([]);
  const itemsPerPage = 12;

  const fetchNFTs = async () => {
    try {
      const res = await instance.get("/me", { withCredentials: true });
      const rawNFTs = res.data.nfts;

      const processed = await Promise.all(
        rawNFTs.map(async (nft) => {
          const meta = await getMetaFromTokenURI(nft.tokenURI);
          return {
            ...nft,
            name: meta?.name || nft.name,
            description: meta?.description || nft.description,
            displayImage: getIpfsUrl(meta?.image),
          };
        })
      );

      setNfts(processed);
    } catch (err) {
      console.error("NFT 불러오기 실패:", err);
    }
  };

  const fetchLikedNFTs = async () => {
    try {
      const res = await instance.get("/user/liked-nfts", { withCredentials: true });
      const rawNFTs = res.data.likedNfts || [];

      const processed = await Promise.all(
        rawNFTs.map(async (nft) => {
          const meta = await getMetaFromTokenURI(nft.tokenURI);
          return {
            ...nft,
            name: meta?.name || nft.name,
            description: meta?.description || nft.description,
            displayImage: getIpfsUrl(meta?.image),
          };
        })
      );

      setLikedNFTs(processed);
    } catch (err) {
      console.error("좋아요 NFT 불러오기 실패:", err);
    }
  };

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await instance.get("/user/me", { withCredentials: true });
        setUser(res.data.user);
        if (res.data.user.profileImage) {
          setPreview(`${API_BASE_URL}${res.data.user.profileImage}`);
        }
      } catch (error) {
        setUser(null);
        navigate("/login");
      }
    };

    fetchUser();
    fetchNFTs();
  }, []);


  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setPreview(URL.createObjectURL(file));

    const formData = new FormData();
    formData.append("profileImage", file);

    try {
      const res = await instance.post("/user/upload-profile", formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });

      setUser((prev) => ({ ...prev, profileImage: res.data.profileImage }));
      setPreview(`${API_BASE_URL}${res.data.profileImage}`);

    } catch (error) {
      console.error("프로필 사진 업로드 오류:", error.message);
    }
  };

  const handleTabChange = (newTab) => {
    setTab(newTab);
    setCurrentPage(1);

    if (newTab === "liked") fetchLikedNFTs();
  };

  const filteredNFTs = (() => {
    if (tab === "owned") return nfts.filter((nft) => !nft.isForSale && !nft.isLazyMinted);
    if (tab === "selling") return nfts.filter((nft) => !!nft.isForSale);
    if (tab === "liked") return likedNFTs;
    return nfts;
  })();

  const totalPages = Math.ceil(filteredNFTs.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedItems = filteredNFTs.slice(startIndex, startIndex + itemsPerPage);

  if (!user) return <Typography>프로필 정보를 불러오는 중...</Typography>;

  return (
    <Container sx={{ mt: 6 }}>
      <Typography variant="h4" fontWeight="bold" gutterBottom color="text.primary">
        Profile
      </Typography>

      {/* 프로필 정보 */}
      <Grid container spacing={3} alignItems="center" sx={{ mb: 6 }}>
        <Grid item xs={12} sm={3}>
          <Avatar src={preview} sx={{ width: 120, height: 120 }} />
        </Grid>
        <Grid item xs={12} sm={5}>
          <Typography>닉네임: {user.name}</Typography>
          <Typography>이메일: {user.email}</Typography>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Stack spacing={1}>
            <Button variant="outlined" fullWidth component="label">
              프로필 사진 변경
              <input type="file" hidden onChange={handleImageUpload} />
            </Button>
            <Button variant="outlined" fullWidth onClick={() => navigate("/wallet")}>
              지갑 관리
            </Button>
          </Stack>
        </Grid>
      </Grid>

      {/* NFT 필터 탭 */}
      <Box sx={{ display: "flex", gap: 2, mb: 4 }}>
        {["all", "owned", "selling", "liked"].map((value) => (
          <Button
            key={value}
            variant={tab === value ? "contained" : "outlined"}
            onClick={() => handleTabChange(value)}
          >
            {value === "all" && "전체 NFT"}
            {value === "owned" && "보유 중"}
            {value === "selling" && "판매 중"}
            {value === "liked" && "좋아요"}
          </Button>
        ))}
      </Box>

      {/* 상태 메시지 */}
      {message && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {message}
        </Typography>
      )}

      {/* NFT 카드 리스트 */}
      <Grid container spacing={3}>
        {paginatedItems.map((nft, index) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={nft._id}>
            <NFTCard item={nft} index={index} onClick={() => navigate(`/nft/${nft._id}`)} />
          </Grid>
        ))}
      </Grid>

      {/* 페이지네이션 */}
      {totalPages > 1 && (
        <Box mt={6} display="flex" justifyContent="center">
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(e, value) => setCurrentPage(value)}
          />
        </Box>
      )}
    </Container>
  );
};

export default ProfilePage;

